// frontend/src/components/calendars/CalendarInputForm.js
import { useEffect, useState } from 'react';
import axios from 'axios';

// 서머타임 여부 확인: true (CDT, Daylight Time(서머타임)), false (CST, Standard Time)
const IS_DST = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  timeZoneName: 'short',
})
  .formatToParts(new Date())
  .some((p) => p.value === 'CDT');

const SESSION_START = IS_DST ? 7 : 8;

// YYYY-MM-DD 유효성 검사
const isValidDateOnly = (dateStr) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return false;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return (
    dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
  );
};

// "YYYY-MM-DD HH:MM" → Date + 로컬 ISO 문자열 (타임존 정보 없음)
const parseDateTimeString = (value, label) => {
  const dtRegex = /^(\d{4})-(\d{2})-(\d{2})\s+([01]\d|2[0-3]):([0-5]\d)$/;
  const match = value.match(dtRegex);
  if (!match) {
    throw new Error(
      `${label}은 "YYYY-MM-DD HH:MM" 형식으로 입력해야 합니다. 예) 2025-11-30 09:30`
    );
  }

  const [, yStr, mStr, dStr, hhStr, mmStr] = match;
  const dateStr = `${yStr}-${mStr}-${dStr}`;
  if (!isValidDateOnly(dateStr)) {
    throw new Error(`${label}의 날짜가 유효하지 않습니다.`);
  }

  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const hh = Number(hhStr);
  const mm = Number(mmStr);

  const jsDate = new Date(y, m - 1, d, hh, mm, 0, 0); // 로컬 KST 서버 기준 가정
  const isoLocal = `${yStr}-${mStr}-${dStr}T${hhStr}:${mmStr}:00`;

  return { jsDate, isoLocal };
};

// ISO 문자열(또는 Date) → "YYYY-MM-DD HH:MM"
const formatForInput = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

export default function CalendarInputForm({
  selectedEvent,
  onClearSelected,
  onChanged,
}) {
  const [editingId, setEditingId] = useState(null); // 선택된 일정 ID
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [isTradingEnabled, setIsTradingEnabled] = useState(false);
  const [message, setMessage] = useState('');

  // 선택된 일정가 바뀔 때 폼에 반영
  useEffect(() => {
    if (selectedEvent) {
      setEditingId(selectedEvent.id ?? null);
      setStartDateTime(formatForInput(selectedEvent.start_datetime));
      setEndDateTime(formatForInput(selectedEvent.end_datetime));
      setEventDescription(selectedEvent.event_description || '');
      setIsTradingEnabled(!!selectedEvent.is_trading_enabled);
      setMessage(
        `
날짜: ${selectedEvent.start_datetime.slice(0, -9)} ~ ${selectedEvent.end_datetime.slice(0, -9)}
내용: ${selectedEvent.event_description}
          `
      );
    } else {
      // 선택 해제 시 폼만 유지할지 초기화할지는 취향에 따라 선택
      setEditingId(null);
      setMessage('');
    }
  }, [selectedEvent]);

  const resetForm = () => {
    setEditingId(null);
    setStartDateTime('');
    setEndDateTime('');
    setEventDescription('');
    setIsTradingEnabled(false);
    setMessage('');
    if (onClearSelected) {
      onClearSelected();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    let startParsed, endParsed;
    try {
      startParsed = parseDateTimeString(startDateTime, '시작 시각');
      endParsed = parseDateTimeString(endDateTime, '종료 시각');
    } catch (err) {
      alert(err.message);
      return;
    }

    const { jsDate: startJs, isoLocal: startIso } = startParsed;
    const { jsDate: endJs, isoLocal: endIso } = endParsed;

    // 시작 < 종료 검증
    if (startJs.getTime() >= endJs.getTime()) {
      alert('종료 시각은 시작 시각보다 이후여야 합니다.');
      return;
    }

    // 설명 최대 글자수(20자)
    const trimmedDescription = eventDescription.trim();
    if (trimmedDescription.length > 20) {
      alert(
        `설명은 최대 20자까지 입력할 수 있습니다. (현재 ${trimmedDescription.length}자)`
      );
      return;
    }

    const payload = {
      start_datetime: startIso, // 예: "2025-11-30T09:30:00"
      end_datetime: endIso, // 예: "2025-12-01T01:02:00"
      event_description: trimmedDescription,
      is_trading_enabled: isTradingEnabled,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/data/trading_calendar/`,
        payload
      );
      const successMsg = '일정 일정 생성/수정 성공! ID: ' + response.data.id;
      setMessage(successMsg);
      alert(successMsg);

      resetForm();
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      let errMsg = '';
      if (error.response && error.response.data) {
        errMsg = JSON.stringify(error.response.data);
      } else {
        errMsg = error.message;
      }
      setMessage('일정 생성/수정 실패: ' + errMsg);
    }
  };

  const handleDelete = async () => {
    if (!editingId) {
      alert(
        '삭제할 일정이 선택되어 있지 않습니다. 캘린더에서 일정을 먼저 선택하세요.'
      );
      return;
    }

    if (
      !window.confirm(
        `정말로 일정(ID: ${editingId})을 삭제하시겠습니까?
        ${message}`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/data/trading_calendar/${editingId}`
      );
      const msg = `일정(ID: ${editingId}) 삭제 완료`;
      setMessage(msg);
      alert(msg);

      resetForm();
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      let errMsg = '';
      if (error.response && error.response.data) {
        errMsg = JSON.stringify(error.response.data);
      } else {
        errMsg = error.message;
      }
      setMessage('일정 삭제 실패: ' + errMsg);
    }
  };

  return (
    <div className="flex flex-col justify-center mx-auto p-6">
      <h1 className="mx-auto mb-4 text-2xl font-semibold">
        거래 불가 캘린더 입력
      </h1>
      <p className="flex flex-col items-center mx-auto">
        주의: 최소 장 개시 전까지 입력되어야 합니다! (오전 {SESSION_START}시)
      </p>
      <p className="flex flex-col items-center mx-auto">
        당일 변경 시 반드시 관리자에게 알려주세요!
      </p>
      <p className="flex flex-col items-center mx-auto pb-6 text-sm text-neutral-700">
        한국시간 기준으로 작동합니다. 거래금지 구간에 자정이 포함되는 경우 종료
        시각 날짜와 형식에 유의하여 입력하세요(24시간 형식).
      </p>

      <form onSubmit={handleSubmit} className="mx-auto space-y-3">
        <div className="flex items-center gap-2">
          <label className="w-40 text-sm text-neutral-700">
            시작 시각 (YYYY-MM-DD HH:MM):
          </label>
          <input
            type="text"
            value={startDateTime}
            onChange={(e) => setStartDateTime(e.target.value)}
            required
            placeholder="2025-11-30 09:30"
            className="block w-72 rounded-md border border-neutral-300 px-3 py-2
                     text-neutral-900 placeholder-neutral-400
                     focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-40 text-sm text-neutral-700">
            종료 시각 (YYYY-MM-DD HH:MM):
          </label>
          <input
            type="text"
            value={endDateTime}
            onChange={(e) => setEndDateTime(e.target.value)}
            required
            placeholder="2025-12-01 01:02"
            className="block w-72 rounded-md border border-neutral-300 px-3 py-2
                     text-neutral-900 placeholder-neutral-400
                     focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-40 text-sm text-neutral-700">설명:</label>
          <input
            type="text"
            value={eventDescription}
            onChange={(e) => setEventDescription(e.target.value)}
            maxLength={20}
            placeholder="예: 추수감사절 휴장, CPI 지표 발표일 등"
            className="block w-72 rounded-md border border-neutral-300 px-3 py-2
                     text-neutral-900 placeholder-neutral-400
                     focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-40 text-sm text-neutral-700">
            거래 가능 여부:
          </label>
          <select
            value={isTradingEnabled ? 'true' : 'false'}
            onChange={(e) => setIsTradingEnabled(e.target.value === 'true')}
            className="block w-56 rounded-md border border-neutral-300 px-3 py-2
                     bg-white text-neutral-900
                     focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="true">거래 가능</option>
            <option value="false">거래 불가</option>
          </select>
        </div>

        <div className="flex flex-col items-center justify-center gap-2">
          <div className="flex gap-2">
            <button
              type="submit"
              className="mt-2 rounded-lg
                     bg-sky-600 px-4 py-2 text-white font-medium
                     hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              등록/수정
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="mt-2 rounded-lg
                     bg-red-600 px-4 py-2 text-white font-medium
                     hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              삭제
            </button>
          </div>
        </div>
      </form>

      {message && (
        <p className="mt-4 text-sm text-neutral-700 text-center">
          선택된 일정 - {message}
        </p>
      )}
    </div>
  );
}
